import React, { useState } from 'react'
import { IWizardProject } from '../types/wizardData'
import { IProject } from '../types/project'
import { IEmployee } from '../../settings/types/employee'
import { IUserDetail } from '../../../types/user'
import { BudgetSelectionStep } from './components/BudgetSelectionStep'
import { BudgetImportFinishStep } from './components/BudgetImportFinishStep'
import WizardSelectTeam from '../components/wizard/WizardSelectTeam'
import WizardSelectBillingProcedure from '../components/wizard/WizardSelectBillingProcedure'

interface Props {
  data: IWizardProject
  setProjectWizardData: (data: IWizardProject) => void
  employees: IEmployee[]
  user: IUserDetail | null
  onClose: () => void
  getProjects?: (queryParams?: any) => void
  getLoggedInUser?: () => Promise<any>
  getProject: (id: number) => Promise<IProject>
  createProjectSuccess: (project: IProject) => void
  filters?: any
}

/**
 * Budget Import Wizard Container
 *
 * Manages the budget import flow:
 * 1. Budget Selection
 * 2. Select Team (reused)
 * 3. Select Billing Procedure (reused)
 * 4. Finish & Execute Transfer
 *
 * @example
 * <BudgetImportWizard {...props} />
 */
export const BudgetImportWizard: React.FC<Props> = (props) => {
  const { data, setProjectWizardData, employees, user, onClose, getProjects, getLoggedInUser, getProject, createProjectSuccess, filters } = props

  console.log('[BudgetImportWizard] Rendered with:', {
    hasGetProjects: !!getProjects,
    hasGetLoggedInUser: !!getLoggedInUser,
    hasGetProject: !!getProject,
    hasCreateProjectSuccess: !!createProjectSuccess,
    filters
  })

  const [wizardStep, setWizardStep] = useState('budget-selection')
  const [wizardFinishedSteps, setWizardFinishedSteps] = useState<number[]>([])

  /**
   * Translates standard wizard step names to budget import step names
   * @param step - Step name to translate
   */
  const translateStepName = (step: string): void => {
    const stepMap: Record<string, string> = {
      'step-three': 'budget-selection',
      'step-four': 'budget-import-select-team',
      'step-five': 'budget-import-select-billing',
      'step-six': 'budget-import-finish'
    }
    setWizardStep(stepMap[step] || step)
  }

  /**
   * Wraps setProjectWizardData to preserve budget_import type
   * @param updatedData - Updated wizard data from reused component
   */
  const wrappedSetProjectWizardData = (updatedData: IWizardProject): void => {
    setProjectWizardData({ ...updatedData, type: 'budget_import' })
  }

  /**
   * Renders current wizard step
   */
  const renderStep = (): JSX.Element | null => {
    const stepProps = {
      open: true,
      data,
      wizardFinishedSteps,
      setWizardFinishedSteps,
      setWizardStep,
      setProjectWizardData,
      onClose
    }

    const reusedStepProps = {
      ...stepProps,
      data: { ...data, type: 'service_provider' },
      setWizardStep: translateStepName,
      setProjectWizardData: wrappedSetProjectWizardData
    }

    switch (wizardStep) {
      case 'budget-selection':
        return <BudgetSelectionStep {...stepProps} />

      case 'budget-import-select-team':
        return (
          <WizardSelectTeam
            {...reusedStepProps}
            employees={employees}
            user={user}
          />
        )

      case 'budget-import-select-billing':
        return (
          <WizardSelectBillingProcedure
            {...reusedStepProps}
            user={user}
          />
        )

      case 'budget-import-finish':
        return <BudgetImportFinishStep {...stepProps} getProjects={getProjects} getLoggedInUser={getLoggedInUser} getProject={getProject} createProjectSuccess={createProjectSuccess} filters={filters} />

      default:
        return null
    }
  }

  return renderStep()
}

export default BudgetImportWizard
